import { AxiosInstance } from 'axios';
import i18next from 'i18next';
import { useQuery } from 'react-query';
import { axiosNoAuth } from './axios';
import { HomeTranslationsType } from '../types/HomeTranslationsType';
import { i18nAppend } from '../helpers/i18nAppend';

const HOME_QUERY_KEY = ['home'] as const;

const filterHomeTranslations = (translations: HomeTranslationsType[]) => {
  return translations.filter((obj) => obj.id != null);
};

const appendHomeTranslations = (homeTranslations: HomeTranslationsType[]) => {
  i18nAppend(homeTranslations, 'herobanner', {
    banner_image: '',
    mobile_kv: '',
    banner_title: '',
    banner_description: '',
    banner_description_mobile: '',
    banner_image_alt_text: '',
    banner_image_disclaimer: '',
  });

  i18nAppend(homeTranslations, 'event_details', {
    event_details_header: '',
    event_details_title: '',
    event_details_description: '',
    event_details_date: '',
    event_details_location: '',
    event_details_button_text: '',
    event_details_button_link: '',
    event_details_button_description: '',
    media_link: '',
    media_thumbnail: ''
  });

  i18nAppend(homeTranslations, 'about_cathay_hackathon', {
    about_section_name: '',
    about_title: '',
    about_kv: '',
    mobile_kv: '',
    about_description: '',
    about_body_text: '',
    about_button_text: '',
    about_button_link: '',
    about_recap_card: [{
      number: '',
      description: ''
    }],
  });

  i18nAppend(homeTranslations, 'detail', {
    application_requirements_card: [{
      title:'',
      icon_link:'',
      description:'',
      icon_alt_text:'',
    }
    ],
    application_requirements_section_name: '',
    application_requirements_title: '',
    application_button_text: '',
    application_link: '',
    application_remarks: '',
  });

  i18nAppend(homeTranslations, 'faq', {
    faq_card: [{
      questions:'',
      answer:''
    }],
    faq_section_name: '',
    faq_title: ''
  });

  i18nAppend(homeTranslations, 'highlight', {
    highlight_card: [{
      title: '',
      image_link: '',
      description: '',
    }],
    highlight_description: '',
    highlight_section_name: '',
    highlight_title: '',
  });

  i18nAppend(homeTranslations, 'winners_group', {
    winner_cards:[{
      team_name:'',
      group_photo_link:'',
      team_description:'',
      winning_place_text:''
    }],
  });

  i18nAppend(homeTranslations, 'journey', {
    timeline_card: [{
      title: '',
      venue: '',
      image_link: '',
      time_range: '',
      description: ''
    }],
    timeline_section_name: '',
    timeline_title: '',
    timeline_count: ''
  });

  i18nAppend(homeTranslations, 'prizes_and_awards', {
    prizes_section_name: '',
    top_winners_title: '',
    top_winners_card: [
      {
        title: '',
        icon_link: '',
        description: '',
        icon_alt_text: '',
      },
    ],
  });

  i18nAppend(homeTranslations, 'sponsor', {
    sponsor_title: '',
    sponsor_type: [{
      title: '',
      type: '',
      value: '',
    }],
    sponsor_logo: [{
      name: '',
      logo: '',
      type: '',
    }],
  });

  i18nAppend(homeTranslations, 'general_site', {
    loading_error_text: ''
  });
};

export const getHomeTranslations = async (axios: AxiosInstance) => {
  const response = await axios.get<HomeTranslationsType[]>('/hackathon-cms/v1/marksite/home');
  const filteredHomeTranslations = filterHomeTranslations(response.data);

  appendHomeTranslations(filteredHomeTranslations);

  return filteredHomeTranslations;
};

export const useHomeTranslationsQuery = () => {
  return useQuery<HomeTranslationsType[]>({
    queryKey: HOME_QUERY_KEY,
    queryFn: () => getHomeTranslations(axiosNoAuth),
    staleTime: Infinity,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

const getDefaultHomeTranslation = (homeTranslations: HomeTranslationsType[]) => {
  return homeTranslations[0];
};

export const getAboutCathayHackathonCardIndexes = (homeTranslations: HomeTranslationsType[]) => {
  return (getDefaultHomeTranslation(homeTranslations)?.about_recap_card ?? []).map((_, index) => index);
};

export const getDetailCardIndexes = (homeTranslations: HomeTranslationsType[]) => {
  return (getDefaultHomeTranslation(homeTranslations)?.application_requirements_card ?? []).map((_, index) => index);
};

export const getFaqCardIndexes = (homeTranslations: HomeTranslationsType[]) => {
  return (getDefaultHomeTranslation(homeTranslations)?.faq_card ?? []).map((_, index) => index);
};

export const getHighlightCardIndexes = (homeTranslations: HomeTranslationsType[]) => {
  return (getDefaultHomeTranslation(homeTranslations)?.highlight_card ?? []).map((_, index) => index);
};

export const getWinnerCardIndexes = (homeTranslations: HomeTranslationsType[]) => {
  return (getDefaultHomeTranslation(homeTranslations)?.winner_cards ?? []).map((_, index) => index);
};

export const getJourneyCardIndexes = (homeTranslations: HomeTranslationsType[]) => {
  return (getDefaultHomeTranslation(homeTranslations)?.timeline_card ?? []).map((_, index) => index);
};

export const getPrizesTranslations = (homeTranslations: HomeTranslationsType[]) => {
  return {
    top_winners_card_idx: (getDefaultHomeTranslation(homeTranslations)?.top_winners_card ?? []).map((_, index) => index),
  };
};

export const getSponsorTranslations = (homeTranslations: HomeTranslationsType[]) => {
  return homeTranslations.find((translation) => translation.languages_code === i18next.language) ?? getDefaultHomeTranslation(homeTranslations);
};

export const getGeneralSiteTranslations = (homeTranslations: HomeTranslationsType[]) => {
  return getDefaultHomeTranslation(homeTranslations)?.loading_error_text ?? '';
};
